// eslint-disable-next-line import/named
import {
  _v6FrameConfigApiVersion,
  _v6FrameConfigVersion,
  _v6SupplierVersion,
  currentSupplierNames,
  getApplicationStartupEvents
} from './application-initialization.js';
import {
  addCurrentUserEventListener,
  getCurrentUser,
  removeCurrentUserEventListener,
  setUserClaims
} from '../../webmodule-common/other/api/current-user.js';
import { addURLResolvers, goStaticURL } from '../../webmodule-common/other/ui/resource-resolver.js';
import {
  AllTestExceptionTypes,
  TestExceptionTypeLabels,
  triggerTestException
} from '../../webmodule-common/other/api/test-exception-api.js';
import { appConfig, appConfigMenuPageItems } from './app-config.js';
import { AppIndex } from '../../webmodule-common/other/app-index.js';
import { appOutOfDate } from '../../webmodule-common/other/debug.js';
import { attachRouter, setRoutes } from '../../webmodule-common/other/router.js';
import { awaitableTemplate } from '../../webmodule-common/other/ui/template-processor.js';
import { claimIdentity, userSecurity } from '../../webmodule-common/other/api/user-security.js';
import { classMap } from 'lit/directives/class-map.js';
import { currentUserClaims, isCynclyStaff } from '../../webmodule-common/other/currentuser-claims.js';
import { customElementTry, registerIconLibrary } from '../../webmodule-common/components/src/webmodule-components.js';

import { fireQuickInformationToast } from '../../webmodule-common/other/toast-away.js';
import { firstValidString, isEmptyOrSpace } from '../../webmodule-common/other/ui/string-helper-functions.js';
import { franchiseeComponentRegistry } from './componentRegistry.js';
import { getApi, getApiFactory } from '../api/api-injector.js';
import { getAssetCdnPath } from '../../webmodule-common/other/common/assets.js';
import { getPageController } from '../../webmodule-common/other/ui/pages/page-base.js';
import { getQuoteSuppliers } from '../quotes/quote-service.js';
import { getSupplier } from './quotes/supplier-selector.js';
import { getSupplierConfigurationOrLastKnown } from '../api/supplier-api-implementation.js';
import { GlobalSupplierNotification } from '../v6config/supplier-services.js';
import { html } from 'lit';
import { iconRegistry } from '../../webmodule-common/other/ui/icons/register-icons.js';
import { information } from '../../webmodule-common/other/ui/modal-option.js';
import { injectThemeClasses, isTestMenuEnabled, isTestSite } from '../../webmodule-common/other/domain.js';
import { LangFranchiseeAppIndex } from '../../localization/scripts/dealer-franchisee/franchisee-app-index-lang.js';
import { lapsedMinutes, sleep } from '../../webmodule-common/other/general/time.js';
import { lockUIandExecute } from '../../webmodule-common/other/ui-lock.js';
import { noSqlDb } from '../../webmodule-common/database/no-sql-db.js';
import { query, state } from 'lit/decorators.js';
import { setUserReloadEvent, verifySession } from '../../webmodule-common/other/user-session-verifier.js';
import { userDataStore } from './common/current-user-data-store.js';
import {
  v6Config,
  v6SupportsVersion,
  v6Util,
  v6VersionMap
} from '../../webmodule-common/interop/interfaces/v6-config.js';

import { webcomponentRegistry } from '../../webmodule-common/other/ui/webcomponent-registry.js';
import { WMEventSource } from '../api/event-source.js';
import type { Branch, ConnectedState, ResultGetSupplierConfiguration } from '../api/dealer-api-interface-franchisee.js';
import type { MenuItem } from '../../webmodule-common/other/app-index';
import type { NoSqlDb } from '../../webmodule-common/database/no-sql-db';
import type { ResolveURLEntry } from '../../webmodule-common/other/ui/resource-resolver';
import type { SupplierInfo } from '../../webmodule-common/interop/webmodule-interop';
import type { TemplateResult } from 'lit';
import type { UserPublicInfo } from '../../webmodule-common/other/api/current-user';

function rebindRouter() {
  const config = appConfig();
  setRoutes(config.routes);
  addURLResolvers(
    config.routes
      .filter(x => x.resolveUrl !== undefined)
      .map(x => {
        return x.resolveUrl as ResolveURLEntry;
      })
  );
}

// this is our concrete top level application, everything else should be generic
@customElementTry('franchisee-app-index')
export class FranchiseeAppIndex extends AppIndex {
  protected noSqlDb: NoSqlDb = noSqlDb;
  // Do not remove below line as this will stop icons from working on our site.
  protected icons = iconRegistry;
  protected webcomponents = webcomponentRegistry;
  protected franchiseeComponents = franchiseeComponentRegistry;
  @query('#main-body')
  private mainBody!: HTMLElement;
  private _supplierOnlineCheckTimer?: NodeJS.Timeout;
  @state()
  private _lastCheckedSupplierOnline?: Date;
  @state()
  private _supplierOnline = GlobalSupplierNotification.getDefaultInstance()?.online;
  @state() private _supplierOfflineMessage = GlobalSupplierNotification.getDefaultInstance()?.offlineMessage;

  constructor() {
    super();
    rebindRouter();

    registerIconLibrary('system', {
      resolver: name => getAssetCdnPath(`/assets/icons/${name}.svg`)
    });

    registerIconLibrary('bootstrap', {
      resolver: name => `https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.4/icons/${name}.svg`
    });

    registerIconLibrary('fa', {
      resolver: name => {
        const filename = name.replace(/^fa[rbs]-/, '');
        let folder = 'regular';
        if (name.startsWith('fas-')) folder = 'solid';
        if (name.startsWith('fab-')) folder = 'brands';
        return `https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6.5.2/svgs/${folder}/${filename}.svg`;
      },
      mutator: svg => svg.setAttribute('fill', 'currentColor')
    });

    this.userDisplayName = getCurrentUser()?.friendlyName;
  }

  async userStateChanged(_user: UserPublicInfo | null) {
    if (appOutOfDate()) return;
    const user = getCurrentUser();
    if (user !== null) {
      const gsn = GlobalSupplierNotification.getDefaultInstance();
      gsn?.addEventListenter(this._supplierStatusCallback);
      this._supplierOnline = gsn?.online;
      this._supplierOfflineMessage = gsn?.offlineMessage;

      await this.updateUIAspectsAfterLogin();
      WMEventSource.getInstance().connectUser();
    } else {
      this.userDisplayName = '';
      WMEventSource.getInstance().disconnect();
    }
  }

  generateBuildNumberText(): TemplateResult {
    const copyEvent = async () => {
      function parts(val: string[], from, count: number): string {
        //Supplier Ver "10.0.13.31.30.380481012"
        return val.slice(from, from + count).join('.');
      }

      function supplierDetails(suppinfo: SupplierInfo, buildnumber: string) {
        const nums = suppinfo.version.split('.');
        return `
### ${LangFranchiseeAppIndex.supplier()} "${suppinfo.description}"

+ ${LangFranchiseeAppIndex.macros()} "${parts(nums, 0, 3)}"
+ ${LangFranchiseeAppIndex.dbInstanceVersion()} "${parts(nums, 3, 1)}"
+ ${LangFranchiseeAppIndex.dbPricingVersion()} "${parts(nums, 4, 1)}"
+ ${LangFranchiseeAppIndex.v6()} "${parts(nums, 5, 1)}"
+ ${LangFranchiseeAppIndex.api()} "${firstValidString(_v6FrameConfigApiVersion, buildnumber)}"
------------------------------------------------------

            `;
      }

      const v6Suppliers = (await v6Config().suppliers()) ?? [];
      const text = `# Dealer's Module (SoftTech dealer CPQ)
      
+ ${LangFranchiseeAppIndex.location()} "${window.location.hostname}"     
+ ${LangFranchiseeAppIndex.dealerBuild()} "${this.buildNumber}"
+ ${LangFranchiseeAppIndex.frameConfig()} "${_v6FrameConfigVersion}"
      
## ${LangFranchiseeAppIndex.supplierDetails()}
${v6Suppliers.map(x => supplierDetails(x, this.buildNumber ?? '')).join('\n')}

            `;
      navigator?.clipboard?.writeText(text);
      await information(text, LangFranchiseeAppIndex.copiedToClipboard());
      fireQuickInformationToast(LangFranchiseeAppIndex.versionDetailsCopied(), 6000);
    };

    function labelSpan(label: string, value: string) {
      return html`<span class="fw-bolder me-2">${label}</span><span class="me-2">${value}</span>`;
    }

    return html`${labelSpan(
        LangFranchiseeAppIndex.build(),
        `${this.buildNumber ?? ''} | ${_v6SupplierVersion} | ${_v6FrameConfigVersion} `
      )} <span class="ms-2 me-2"><icon-copy @click=${copyEvent}></icon-copy></span>`;
  }

  serviceOnlineTemplate() {
    return this._supplierOnline
      ? html``
      : html` <div class="supplier-offline-alert">
          <span>${this._supplierOfflineMessage}</span>
        </div>`;
  }

  connectedCallback(): void {
    super.connectedCallback();
    const gsn = GlobalSupplierNotification.getDefaultInstance();
    gsn?.addEventListenter(this._supplierStatusCallback);
    this._supplierOnline = gsn?.online;
    this._supplierOfflineMessage = gsn?.offlineMessage;

    addCurrentUserEventListener(this);
    setUserReloadEvent(async () => {
      const user = getCurrentUser();
      if (user) {
        await userDataStore.reLoadFranchisee();
        await this.updateUIAspectsAfterLogin();
      } else {
        userDataStore.clear();
      }

      this.requestUpdate();
    });
    if (gsn) gsn.extraText = LangFranchiseeAppIndex.testingConnection();
    this._supplierOnlineCheckTimer = setTimeout(this._supplierOnlineCheckEvent, 60000);
  }

  disconnectedCallback(): void {
    clearTimeout(this._supplierOnlineCheckTimer);
    this._supplierOnlineCheckTimer = undefined;
    GlobalSupplierNotification.getDefaultInstance()?.remEventListenter(this._supplierStatusCallback);
    super.disconnectedCallback();
    removeCurrentUserEventListener(this);
  }

  userNameMenuContent(): unknown {
    return html` <webmodule-icon library="fa" name="fas-user" slot="prefix"></webmodule-icon
      >${this.userDisplayName?.trim()}`;
  }

  aboveUserNameTemplate(): unknown {
    const branchName = userDataStore.loaded ? userDataStore.defaultBranch.name.trim() : '';

    return userDataStore.loaded
      ? html`
          <webmodule-dropdown placement="bottom">
            <webmodule-button variant="text" slot="trigger" caret>
              <webmodule-icon library="fa" name="fas-building" slot="prefix"></webmodule-icon>
              ${branchName}
            </webmodule-button>
            <webmodule-menu> ${this.customBranchMenuElements()}</webmodule-menu>
          </webmodule-dropdown>
        `
      : html``;
  }

  globalSupportInformation(): string | TemplateResult {
    const cu = getCurrentUser();
    if (!cu) return '';
    const cc = currentUserClaims();
    if (cc.isCynclyStaff) {
      return cc.isAgent // cyncly staff can log in as a dealer or supplier depending on support needed
        ? `CYNCLY Support AS SUPPLIER connected to "${
            (cu as any).tenantName
          }", Using Supplier(s) ${currentSupplierNames}`
        : `CYNCLY Support AS DEALER connected to "${(cu as any).tenantName}", Using Supplier ${currentSupplierNames}`;
    } else if (cc.isAgent) {
      return LangFranchiseeAppIndex.supplierSupportConnected({ tenantName: (cu as any).tenantName });
    }
    return '';
  }

  customUserMenuElements(): TemplateResult {
    return html``;
  }

  customBranchMenuElements(): TemplateResult {
    const doNothing = (e: Event) => {
      e.stopImmediatePropagation();
      e.preventDefault();
    };
    const switchBranchEvent = (branch: Branch) => () => {
      this.changeBranch(branch);
    };
    const branchTemplate = (branch: Branch) => {
      const isSelected = branch.id === userDataStore.defaultBranch.id;
      return html`
        <webmodule-menu-item
          class="${classMap({
            'fw-bolder': isSelected
          })}"
          @click=${switchBranchEvent(branch)}
        >
          <webmodule-icon
            class="${classMap({
              'text-primary': isSelected
            })}"
            slot="prefix"
            library="fa"
            name="fas-caret-right"
          ></webmodule-icon>
          ${branch.name}
        </webmodule-menu-item>
      `;
    };
    const switchBranchTemplate = () => {
      return userDataStore
        .getUserBranches()

        .map(x => branchTemplate(x));
    };
    if (!getCurrentUser() || !userDataStore.loaded) return html``;
    if (userSecurity().claimIsTrue(claimIdentity.allBranches) && userDataStore.getUserBranches().length > 1) {
      return html`
        <webmodule-menu-label>${LangFranchiseeAppIndex.branchPlural()}</webmodule-menu-label>
        ${switchBranchTemplate()}
        <webmodule-divider></webmodule-divider>
      `;
    } else
      return html` <webmodule-menu-label>${LangFranchiseeAppIndex.branchPlural()}</webmodule-menu-label>

        <webmodule-menu-item @click=${doNothing}>
          <webmodule-icon class="text-primary" slot="prefix" library="fa" name="fas-caret-right"></webmodule-icon>
          ${userDataStore.defaultBranch.name}
        </webmodule-menu-item>
        <webmodule-divider></webmodule-divider>`;
  }

  async changeBranch(branch: Branch) {
    await lockUIandExecute(async () => {
      if (!(await getPageController().pagesCanClose())) return;
      goStaticURL('');
      await sleep(300);
      userDataStore.defaultBranch = branch;
      await userDataStore.setV6OverrideKey();

      //the line below is executed in the line above

      await getPageController().disposeAndRefresh();
      this.requestUpdate();
    });
  }

  async disconnectUser() {
    await getPageController().disposeAll();
    try {
      await getApi().post<ConnectedState>('api/Franchisee/DisconnectUser', {});
      setUserClaims({});
    } catch {
      console.log(`Error disconnecting user`);
    }
    return true;
  }

  public updateLogoTemplate() {
    if (this.outOfDate) return;
    const api = getApi();
    try {
      const logoURl = isTestSite()
        ? api.fullUrl(`api/file/${userDataStore.franchisee.negativeLogoVirtualPath}`)
        : api.fullUrl(`api/file/${userDataStore.franchisee.positiveLogoVirtualPath}`);
      this.logoTemplate = userDataStore.franchisee.positiveLogoVirtualPath
        ? html`<img src=${logoURl} crossorigin="anonymous" class="img-fluid" alt="Brand" width="90" />`
        : html` <svg width="1" height="1"></svg>`;
    } catch {
      //may be doing this after a failed login or out of date issue
    }
  }

  async getSupplierResourceLogo() {
    if (this.outOfDate) return;
    const api = getApiFactory().supplier();
    try {
      const supplierId = (await getSupplier()).supplierId;
      const result: ResultGetSupplierConfiguration | null = await getSupplierConfigurationOrLastKnown(api, supplierId);

      if (result && !isEmptyOrSpace(result.resourceUrl) && !isEmptyOrSpace(result.resourceLogoAltText)) {
        let linkContent: TemplateResult | string = result.resourceLogoAltText;
        if (!isEmptyOrSpace(result.resourceLogoVirtualPath)) {
          const logoURL = api.api.fullUrl(`api/file/${result.resourceLogoVirtualPath}`);
          linkContent = html`<img
            src=${logoURL}
            crossorigin="anonymous"
            class="img-fluid"
            alt=${result.resourceLogoAltText}
            width="90"
          />`;
        }
        this.supplierResourceLinkTemplate = html`<a
          href=${result.resourceUrl}
          target="_blank"
          rel="noreferrer noopener"
          class="supplier-link-href"
          >${linkContent}</a
        >`;
      }
    } catch {
      //may be doing this after a failed login or out of date issue
    }
  }

  protected restartV6MenuItem(): TemplateResult {
    const getSupplierTemplates = async () => {
      const eventRestartV6 = (supplierId: string) => {
        return async () => await v6Util().restartV6Service(supplierId);
      };

      const v6Suppliers = (await v6Config().suppliers()) ?? [];

      if (!v6SupportsVersion(v6VersionMap.hasRestartV6)) return html``;

      return html`${v6Suppliers?.map(x => {
        return html` <webmodule-menu-item @click=${eventRestartV6(x.id)}
          >${LangFranchiseeAppIndex.restartV6Service({ description: x.description })}
        </webmodule-menu-item>`;
      })}`;
    };

    return html`${awaitableTemplate(getSupplierTemplates())}`;
  }

  protected extendedUserMenuItems() {
    const hasCynclyAccess = isCynclyStaff();
    const hasTestMenuAccess = hasCynclyAccess || isTestMenuEnabled();
    const hasV6Restart = hasCynclyAccess || currentUserClaims().trueClaim('restart-v6-service');

    if (!hasV6Restart && !hasTestMenuAccess) {
      return undefined;
    }

    const restartMenuItem = hasV6Restart ? this.restartV6MenuItem() : html``;
    const exceptionTestMenu = hasTestMenuAccess ? this.exceptionTestMenuItem() : html``;

    return html`
      <webmodule-divider></webmodule-divider>
      ${restartMenuItem} ${exceptionTestMenu}
    `;
  }

  /**
   * Creates a submenu for testing various exception types.
   * Only visible to Cyncly staff for testing error handling.
   */
  protected exceptionTestMenuItem(): TemplateResult {
    const triggerException = (exceptionType: number) => {
      return async () => {
        console.log(`Testing exception type: ${TestExceptionTypeLabels[exceptionType]}`);
        try {
          await triggerTestException(exceptionType);
        } catch (e) {
          // Expected - the exception is handled by the API error handler
          console.log('Exception triggered and handled:', e);
        }
      };
    };

    return html`
      <webmodule-menu-item>
        <webmodule-icon slot="prefix" library="fa" name="fas-bug"></webmodule-icon>
        Test Error Handling
        <webmodule-menu slot="submenu">
          ${AllTestExceptionTypes.map(
            exType => html`
              <webmodule-menu-item @click=${triggerException(exType)}>
                ${TestExceptionTypeLabels[exType]}
              </webmodule-menu-item>
            `
          )}
        </webmodule-menu>
      </webmodule-menu-item>
    `;
  }

  protected getMenuItems(): MenuItem[] {
    return appConfigMenuPageItems(userDataStore.loaded ? userDataStore.isFabricatorMode : false);
  }

  protected async firstUpdated() {
    attachRouter(this.mainBody);
    let interval;

    await getApplicationStartupEvents();
    const verifyUser = async () => {
      clearInterval(interval);
      await verifySession();
      interval = setTimeout(verifyUser, 500);
    };
    interval = setTimeout(verifyUser, 500);
    injectThemeClasses();
  }

  private _supplierOnlineCheckEvent = async () => {
    const gsn = GlobalSupplierNotification.getDefaultInstance();
    if (!gsn) return;
    if (!gsn.online) {
      if (!this._lastCheckedSupplierOnline || lapsedMinutes(this._lastCheckedSupplierOnline) > 5) {
        const suppliers = await getQuoteSuppliers(true);
        if (suppliers.length > 0 && suppliers[0].online) {
          this._lastCheckedSupplierOnline = undefined;
        } else this._lastCheckedSupplierOnline = new Date();
      }
      gsn.extraText = LangFranchiseeAppIndex.testingConnectionLastCheck({
        time: this._lastCheckedSupplierOnline?.toLocaleTimeString() ?? ''
      });
    } else this._lastCheckedSupplierOnline = undefined;
    this._supplierOnlineCheckTimer = setTimeout(this._supplierOnlineCheckEvent, 60000);
  };

  private _supplierStatusCallback = () => {
    try {
      this._supplierOnline = GlobalSupplierNotification.getInstance().online;
      this._supplierOfflineMessage = GlobalSupplierNotification.getInstance().offlineMessage;
    } catch {
      /* empty */
    }
  };

  private async updateUIAspectsAfterLogin() {
    await userDataStore.loadCoreDetailsAfterLogin();
    this.updateLogoTemplate();
    this.userDisplayName = getCurrentUser()?.friendlyName;
    this.requestUpdate();
    await this.getSupplierResourceLogo();
  }
}
